import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { ProductCard } from '../../components/admin/ProductCard';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import {
  useGetProductsQuery,
  useDeleteProductMutation,
  useUpdateProductStatusMutation,
  useGetHideProductsStatusQuery,
  useUpdateHideProductsStatusMutation,
} from '../../store/api/adminApi';
import { useGetAdminCategoriesQuery } from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';
import { formatDate } from '../../utils/dateFormatter';
import { IS_TENANT_MODE } from '../../config/tenantMode';

export function AdminProductsPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const isTenantMode = IS_TENANT_MODE;
  const [page, setPage] = useState(1);
  const limit = 20;
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined);
  const [statusFilter, setStatusFilter] = useState<'available' | 'sold' | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState('');

  const { data, isLoading, error, refetch } = useGetProductsQuery({
    categoryId: categoryFilter,
    status: statusFilter,
    page,
    limit,
  });

  // Get categories for filter
  const { data: categoriesData } = useGetAdminCategoriesQuery({ includeHidden: true });

  const [deleteProduct, { isLoading: isDeleting }] = useDeleteProductMutation();
  const [updateProductStatus, { isLoading: isUpdatingStatus }] = useUpdateProductStatusMutation();

  // Dialog states
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    productId: string | null;
    productName: string;
  }>({ isOpen: false, productId: null, productName: '' });

  const [statusDialog, setStatusDialog] = useState<{
    isOpen: boolean;
    productId: string | null;
    productName: string;
    currentStatus: 'available' | 'sold';
  }>({ isOpen: false, productId: null, productName: '', currentStatus: 'available' });

  // Hide Products / SSN visibility
  const [hideDialogOpen, setHideDialogOpen] = useState(false);
  const { data: hideStatusData } = useGetHideProductsStatusQuery();
  const [updateHideProducts, { isLoading: isUpdatingHide }] = useUpdateHideProductsStatusMutation();
  const currentHide = hideStatusData?.data.hideProducts || { bot: false, website: false };
  const currentSSN = hideStatusData?.data.hideSSN || { bot: false, website: false };

  const handleToggleHide = async (target: 'bot' | 'website') => {
    try {
      const newValue = target === 'bot' ? !currentHide.bot : !currentHide.website;
      await updateHideProducts({ [target]: newValue }).unwrap();
      toast.success(
        target === 'bot'
          ? t('admin.products.hideProducts.botToggled', { status: newValue ? t('common.hidden') : t('common.visible') })
          : t('admin.products.hideProducts.websiteToggled', { status: newValue ? t('common.hidden') : t('common.visible') })
      );
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || t('admin.products.hideProducts.error'));
    }
  };

  const handleToggleSSN = async (target: 'bot' | 'website') => {
    try {
      const key = target === 'bot' ? 'ssnBot' : 'ssnWebsite';
      const newValue = target === 'bot' ? !currentSSN.bot : !currentSSN.website;
      await updateHideProducts({ [key]: newValue }).unwrap();
      toast.success(
        target === 'bot'
          ? t('admin.products.hideSSN.botToggled', { status: newValue ? t('common.hidden') : t('common.visible') })
          : t('admin.products.hideSSN.websiteToggled', { status: newValue ? t('common.hidden') : t('common.visible') })
      );
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(error?.data?.message || error?.message || t('admin.products.hideProducts.error'));
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (productId: string, productName: string) => {
    setDeleteDialog({ isOpen: true, productId, productName });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.productId) return;

    try {
      await deleteProduct(deleteDialog.productId).unwrap();
      toast.success(t('admin.products.deleteSuccess'));
      setDeleteDialog({ isOpen: false, productId: null, productName: '' });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.products.deleteError')
      );
    }
  };

  const handleStatusClick = (productId: string, productName: string, currentStatus: 'available' | 'sold') => {
    setStatusDialog({ isOpen: true, productId, productName, currentStatus });
  };

  const handleStatusConfirm = async () => {
    if (!statusDialog.productId) return;

    try {
      await updateProductStatus({
        productId: statusDialog.productId,
        data: {
          status: statusDialog.currentStatus === 'available' ? 'sold' : 'available',
        },
      }).unwrap();
      toast.success(t('admin.products.statusSuccess'));
      setStatusDialog({ isOpen: false, productId: null, productName: '', currentStatus: 'available' });
      void refetch();
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.products.statusError')
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen">
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <h2 className="text-2xl font-heading font-bold text-danger mb-4">
            {t('admin.products.error.title')}
          </h2>
          <p className="text-neutral-medium font-body mb-6">
            {'data' in (error as { data?: { message?: string } })
              ? ((error as { data?: { message?: string } })?.data?.message || t('errors.network'))
              : t('errors.network')}
          </p>
          <button
            onClick={() => void refetch()}
            className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  const products = data?.data.products || [];
  const pagination = data?.data.pagination;

  // Filter products by search term
  const filteredProducts = products.filter((product) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      product.name.toLowerCase().includes(search) ||
      product.description?.toLowerCase().includes(search) ||
      product.value.toLowerCase().includes(search)
    );
  });

  // Get all categories for filter
  const categories = categoriesData?.data.categories || [];

  // Statistics
  const totalProducts = pagination?.total || 0;
  const availableProducts = products.filter((p) => p.status === 'available').length;
  const soldProducts = products.filter((p) => p.status === 'sold').length;

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
            {t('admin.products.title')}
          </h1>
          <p className="text-neutral-medium text-lg font-body">
            {t('admin.products.subtitle')}
          </p>
        </div>
        <Link
          to="/admin/products/new"
          className="px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300"
        >
          {t('admin.products.createNew')}
        </Link>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card-glass rounded-lg p-4 border border-neutral-dark">
          <div className="text-sm text-neutral-medium font-body mb-1">
            {t('admin.products.stats.total')}
          </div>
          <div className="text-2xl font-heading font-bold text-brand-accent">
            {totalProducts}
          </div>
        </div>
        <div className="card-glass rounded-lg p-4 border border-neutral-dark">
          <div className="text-sm text-neutral-medium font-body mb-1">
            {t('admin.products.stats.available')}
          </div>
          <div className="text-2xl font-heading font-bold text-green-500">
            {availableProducts}
          </div>
        </div>
        <div className="card-glass rounded-lg p-4 border border-neutral-dark">
          <div className="text-sm text-neutral-medium font-body mb-1">
            {t('admin.products.stats.sold')}
          </div>
          <div className="text-2xl font-heading font-bold text-neutral-light">
            {soldProducts}
          </div>
        </div>
      </div>

      {/* Hide Products Visibility Control */}
      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <div className="text-lg font-heading font-semibold text-neutral-light">
            {t('admin.products.hideProducts.title')}
          </div>
          <div className="text-sm text-neutral-medium font-body mt-1 flex flex-wrap gap-3">
            <span className={currentHide.bot ? 'text-danger' : 'text-green-500'}>
              {t('admin.products.hideProducts.botLabel')}: {currentHide.bot ? t('common.hidden') : t('common.visible')}
            </span>
            {!isTenantMode && (
              <span className={currentHide.website ? 'text-danger' : 'text-green-500'}>
                {t('admin.products.hideProducts.websiteLabel')}: {currentHide.website ? t('common.hidden') : t('common.visible')}
              </span>
            )}
            {!isTenantMode && (
              <span className={currentSSN.bot ? 'text-danger' : 'text-green-500'}>
                SSN {t('admin.products.hideProducts.botLabel')}: {currentSSN.bot ? t('common.hidden') : t('common.visible')}
              </span>
            )}
            {!isTenantMode && (
              <span className={currentSSN.website ? 'text-danger' : 'text-green-500'}>
                SSN {t('admin.products.hideProducts.websiteLabel')}: {currentSSN.website ? t('common.hidden') : t('common.visible')}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => setHideDialogOpen(true)}
          className="px-5 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 hover:text-brand-accent transition-all duration-300 border border-neutral-dark"
        >
          {t('admin.products.hideProducts.manageButton')}
        </button>
      </div>

      {/* Hide Products Modal */}
      {hideDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setHideDialogOpen(false)}>
          <div className="card-glass rounded-2xl shadow-2xl p-6 sm:p-8 max-w-md w-full mx-4 border border-neutral-dark animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-2xl font-heading font-bold text-brand-accent mb-2">
              {t('admin.products.hideProducts.modalTitle')}
            </h3>
            <p className="text-neutral-medium font-body mb-6">
              {isTenantMode
                ? 'تحكم في إظهار زر المنتجات في بوت التليجرام (الموقع لوحة أدمن فقط)'
                : t('admin.products.hideProducts.modalDesc')}
            </p>

            <div className="space-y-4">
              {/* Toggle: Hide from Bot */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-neutral-dark/50 border border-neutral-dark">
                <div>
                  <div className="font-heading font-semibold text-neutral-light">
                    {t('admin.products.hideProducts.botLabel')}
                  </div>
                  <div className="text-sm text-neutral-medium font-body">
                    {t('admin.products.hideProducts.botDesc')}
                  </div>
                </div>
                <button
                  onClick={() => handleToggleHide('bot')}
                  disabled={isUpdatingHide}
                  className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                    currentHide.bot ? 'bg-danger' : 'bg-green-500'
                  } ${isUpdatingHide ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                    currentHide.bot ? 'translate-x-7' : 'translate-x-0'
                  }`} />
                </button>
              </div>

              {/* Website storefront does not exist for tenant merchants — bot only */}
              {!isTenantMode && (
                <div className="flex items-center justify-between p-4 rounded-lg bg-neutral-dark/50 border border-neutral-dark">
                  <div>
                    <div className="font-heading font-semibold text-neutral-light">
                      {t('admin.products.hideProducts.websiteLabel')}
                    </div>
                    <div className="text-sm text-neutral-medium font-body">
                      {t('admin.products.hideProducts.websiteDesc')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleHide('website')}
                    disabled={isUpdatingHide}
                    className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                      currentHide.website ? 'bg-danger' : 'bg-green-500'
                    } ${isUpdatingHide ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      currentHide.website ? 'translate-x-7' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              )}

              {!isTenantMode && (
              <div className="border-t border-neutral-dark pt-4">
                <div className="text-sm font-heading font-semibold text-brand-accent mb-3">
                  {t('admin.products.hideSSN.sectionTitle')}
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-neutral-dark/50 border border-neutral-dark mb-3">
                  <div>
                    <div className="font-heading font-semibold text-neutral-light">
                      {t('admin.products.hideSSN.botLabel')}
                    </div>
                    <div className="text-sm text-neutral-medium font-body">
                      {t('admin.products.hideSSN.botDesc')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleSSN('bot')}
                    disabled={isUpdatingHide}
                    className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                      currentSSN.bot ? 'bg-danger' : 'bg-green-500'
                    } ${isUpdatingHide ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      currentSSN.bot ? 'translate-x-7' : 'translate-x-0'
                    }`} />
                  </button>
                </div>

                <div className="flex items-center justify-between p-4 rounded-lg bg-neutral-dark/50 border border-neutral-dark">
                  <div>
                    <div className="font-heading font-semibold text-neutral-light">
                      {t('admin.products.hideSSN.websiteLabel')}
                    </div>
                    <div className="text-sm text-neutral-medium font-body">
                      {t('admin.products.hideSSN.websiteDesc')}
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggleSSN('website')}
                    disabled={isUpdatingHide}
                    className={`relative w-14 h-7 rounded-full transition-colors duration-300 ${
                      currentSSN.website ? 'bg-danger' : 'bg-green-500'
                    } ${isUpdatingHide ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-300 ${
                      currentSSN.website ? 'translate-x-7' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>
              )}
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={() => setHideDialogOpen(false)}
                className="px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all duration-300"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="card-glass rounded-lg p-4 mb-6 border border-neutral-dark">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.products.filters.search')}
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t('admin.products.filters.searchPlaceholder')}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            />
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.products.filters.category')}
            </label>
            <select
              value={categoryFilter || ''}
              onChange={(e) => {
                setCategoryFilter(e.target.value || undefined);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.products.filters.all')}</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {currentLanguage === 'ar' ? cat.name : cat.nameEn}
                </option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.products.filters.status')}
            </label>
            <select
              value={statusFilter || ''}
              onChange={(e) => {
                setStatusFilter((e.target.value as 'available' | 'sold' | '') || undefined);
                setPage(1);
              }}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
            >
              <option value="">{t('admin.products.filters.all')}</option>
              <option value="available">{t('admin.products.status.available')}</option>
              <option value="sold">{t('admin.products.status.sold')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Products List */}
      {filteredProducts.length === 0 ? (
        <div className="card-glass rounded-2xl shadow-2xl p-8 text-center">
          <p className="text-neutral-medium text-lg font-body">
            {t('admin.products.empty')}
          </p>
        </div>
      ) : (
        <>
          {/* Mobile Cards View */}
          <div className="md:hidden space-y-4 mb-6">
            {filteredProducts.map((product) => (
              <ProductCard
                key={product._id}
                product={product}
                onStatusChange={handleStatusClick}
                onDelete={handleDeleteClick}
                isUpdatingStatus={isUpdatingStatus}
                isDeleting={isDeleting}
              />
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block card-glass rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-neutral-dark/50">
                  <tr>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.products.table.name')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.products.table.category')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.products.table.value')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.products.table.status')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-left text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.products.table.created')}
                    </th>
                    <th className="px-3 sm:px-6 py-4 text-center text-xs sm:text-sm font-heading font-semibold text-brand-accent whitespace-nowrap">
                      {t('admin.products.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-dark">
                  {filteredProducts.map((product) => {
                    const category = typeof product.categoryId === 'object' ? product.categoryId : null;
                    const categoryName = category
                      ? currentLanguage === 'ar'
                        ? category.name
                        : category.nameEn
                      : '';

                    return (
                      <tr key={product._id} className="hover:bg-neutral-dark/30 transition-colors">
                        <td className="px-3 sm:px-6 py-4">
                          <div>
                            <div className="font-heading font-semibold text-neutral-light">
                              {product.name}
                            </div>
                            {product.description && (
                              <div className="text-sm text-neutral-medium font-body mt-1 line-clamp-1 whitespace-pre-line">
                                {product.description}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          {category ? (
                            <Link
                              to={`/admin/categories/${category._id}/edit`}
                              className="text-brand-accent hover:underline font-body text-sm"
                            >
                              {categoryName}
                            </Link>
                          ) : (
                            <span className="text-neutral-medium font-body text-sm">-</span>
                          )}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="font-mono text-xs sm:text-sm text-neutral-light font-body">
                            {product.value}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-xs font-body ${
                              product.status === 'available'
                                ? 'bg-green-500/20 text-green-500'
                                : 'bg-neutral-dark text-neutral-medium'
                            }`}
                          >
                            {product.status === 'available'
                              ? t('admin.products.status.available')
                              : t('admin.products.status.sold')}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span className="text-xs sm:text-sm text-neutral-medium font-body">
                            {formatDate(product.createdAt, 'PP h:mm a', currentLanguage, 'Asia/Damascus')}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4">
                          <div className="flex items-center justify-center gap-1 sm:gap-2 flex-wrap">
                            <Link
                              to={`/admin/products/${product._id}/edit`}
                              className="px-3 py-1 bg-brand-accent/20 text-brand-accent rounded-lg hover:bg-brand-accent/30 transition-colors text-sm font-body"
                              title={t('admin.products.actions.edit')}
                            >
                              {t('admin.products.actions.edit')}
                            </Link>
                            <button
                              onClick={() => handleStatusClick(product._id, product.name, product.status)}
                              disabled={isUpdatingStatus}
                              className={`px-3 py-1 rounded-lg transition-colors text-sm font-body ${
                                product.status === 'available'
                                  ? 'bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30'
                                  : 'bg-green-500/20 text-green-500 hover:bg-green-500/30'
                              }`}
                              title={
                                product.status === 'available'
                                  ? t('admin.products.actions.markSold')
                                  : t('admin.products.actions.markAvailable')
                              }
                            >
                              {product.status === 'available'
                                ? t('admin.products.actions.markSold')
                                : t('admin.products.actions.markAvailable')}
                            </button>
                            <button
                              onClick={() => handleDeleteClick(product._id, product.name)}
                              disabled={isDeleting}
                              className="px-3 py-1 bg-danger/20 text-danger rounded-lg hover:bg-danger/30 transition-colors text-sm font-body disabled:opacity-50"
                              title={t('admin.products.actions.delete')}
                            >
                              {t('admin.products.actions.delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('catalog.pagination.previous')}
              </button>

              <div className="px-4 py-2 bg-neutral-dark/50 text-neutral-light font-body rounded-lg">
                {t('catalog.pagination.page')} {pagination.page} {t('catalog.pagination.of')} {pagination.pages}
              </div>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                className="px-4 py-2 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('catalog.pagination.next')}
              </button>
            </div>
          )}

          {/* Pagination Info */}
          {pagination && (
            <div className="text-center text-neutral-medium font-body mt-4">
              {t('catalog.pagination.showing')} {filteredProducts.length} {t('catalog.pagination.of')} {pagination.total} {t('admin.products.pagination.products')}
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={() => setDeleteDialog({ isOpen: false, productId: null, productName: '' })}
        onConfirm={handleDeleteConfirm}
        title={t('admin.products.deleteTitle')}
        message={t('admin.products.deleteConfirm', { name: deleteDialog.productName })}
        confirmText={t('admin.products.actions.delete')}
        type="danger"
        isLoading={isDeleting}
      />

      {/* Status Change Confirmation Dialog */}
      <ConfirmDialog
        isOpen={statusDialog.isOpen}
        onClose={() => setStatusDialog({ isOpen: false, productId: null, productName: '', currentStatus: 'available' })}
        onConfirm={handleStatusConfirm}
        title={
          statusDialog.currentStatus === 'available'
            ? t('admin.products.markSoldTitle')
            : t('admin.products.markAvailableTitle')
        }
        message={
          statusDialog.currentStatus === 'available'
            ? t('admin.products.markSoldConfirm', { name: statusDialog.productName })
            : t('admin.products.markAvailableConfirm', { name: statusDialog.productName })
        }
        confirmText={
          statusDialog.currentStatus === 'available'
            ? t('admin.products.actions.markSold')
            : t('admin.products.actions.markAvailable')
        }
        type="info"
        isLoading={isUpdatingStatus}
      />
    </div>
  );
}

